import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router-dom';
import { apiFetch } from '../../lib/api';
import { SkeletonRows } from '../skeleton';

interface ArchiveBatchSummary {
  readonly id: string;
  readonly createdAt: string;
  readonly createdByName: string | null;
  readonly note: string | null;
  readonly itemCount: number;
}

/**
 * 归档批次列表（0023）：批量导入基本不可逆的安全网——每次"批量归档"动作
 * 分组进一条记录，这里能看到都归档过什么、点进去查看成员并恢复。
 */
export function ArchiveBatchesPage(): React.JSX.Element {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const fmt = new Intl.DateTimeFormat(i18n.language, { dateStyle: 'medium', timeStyle: 'short' });

  const { data, isPending } = useQuery({
    queryKey: ['products', 'archive-batches'],
    queryFn: () => apiFetch<{ items: ArchiveBatchSummary[] }>('/products/archive-batches'),
  });

  return (
    <div>
      <div className="row row--between">
        <div>
          <button className="btn btn--ghost btn--sm" onClick={() => navigate('/products')}>
            ← {t('nav.products')}
          </button>
          <h1 style={{ fontSize: 'var(--text-2xl)' }}>{t('products.archiveBatchesTitle')}</h1>
        </div>
      </div>

      {isPending ? (
        <SkeletonRows />
      ) : (data?.items.length ?? 0) === 0 ? (
        <div className="empty-state">
          <p>{t('products.archiveBatchesEmpty')}</p>
        </div>
      ) : (
        <div className="table" style={{ display: 'block' }}>
          <div
            className="row"
            style={{ fontWeight: 600, padding: 'var(--space-2) 0', borderBottom: '1px solid var(--color-border)' }}
          >
            <span style={{ width: '180px' }}>{t('products.archiveBatchColCreatedAt')}</span>
            <span style={{ width: '160px' }}>{t('products.archiveBatchColCreatedBy')}</span>
            <span style={{ width: '100px' }}>{t('products.archiveBatchColCount')}</span>
            <span style={{ flex: 1 }}>{t('products.archiveBatchColNote')}</span>
          </div>
          {data!.items.map((batch) => (
            <Link
              key={batch.id}
              to={`/products/archive-batches/${batch.id}`}
              className="tree__row"
              style={{ height: '48px', textDecoration: 'none', color: 'inherit' }}
            >
              <span style={{ width: '180px' }}>{fmt.format(new Date(batch.createdAt))}</span>
              <span style={{ width: '160px' }}>{batch.createdByName ?? '—'}</span>
              <span style={{ width: '100px' }}>{batch.itemCount}</span>
              <span style={{ flex: 1 }}>{batch.note ?? '—'}</span>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}
